import { Form, Input, Select, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { createLead, type LeadSource } from './api';
import { SOURCE_LABEL } from './stage';

const { Text } = Typography;

interface LeadFormValues {
  name: string;
  phone: string;
  email?: string;
  source: LeadSource;
  campaign?: string;
}

export function LeadCreateDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (uid: string, joinedExisting: boolean) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<LeadFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    setSubmitting(true);
    setFailure(null);
    try {
      const before = Date.now();
      const lead = await createLead(values);
      form.resetFields();
      // The API silently joins a repeat enquiry to the existing lead rather than
      // rejecting it. Telling the advisor which happened avoids the confusion of
      // "I added a lead and got someone else's record".
      const joinedExisting = new Date(lead.createdAt).getTime() < before - 5_000;
      onCreated(lead.uid, joinedExisting);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : 'Could not capture the lead.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionDrawer
      open={open}
      title={t('crm.create')}
      submitLabel={t('crm.create')}
      submitting={submitting}
      onSubmit={() => void submit()}
      onClose={() => {
        setFailure(null);
        onClose();
      }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ source: 'WALK_IN' }}>
        <Form.Item
          name="name"
          label={t('fields.name')}
          rules={[{ required: true, min: 2, message: 'Enter the enquirer’s name.' }]}
        >
          <Input size="large" autoFocus />
        </Form.Item>

        <Form.Item
          name="phone"
          label={t('fields.phone')}
          rules={[{ required: true, min: 7, message: 'Enter a contact number.' }]}
          extra={t('crm.phoneHelp')}
        >
          <Input size="large" inputMode="tel" />
        </Form.Item>

        <Form.Item name="email" label={t('fields.email')} rules={[{ type: 'email' }]}>
          <Input size="large" inputMode="email" />
        </Form.Item>

        <Form.Item name="source" label={t('crm.fields.source')} rules={[{ required: true }]}>
          <Select
            size="large"
            options={Object.entries(SOURCE_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </Form.Item>

        <Form.Item name="campaign" label={t('crm.fields.campaign')}>
          <Input size="large" placeholder="Merdeka 2026" />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}
